import { createAsyncThunk } from '@reduxjs/toolkit';
import { logger } from 'common/services';
import { selectMyCommunityId } from 'community/api';
import { orderBy } from 'lodash-es';
import { addAppErrorMessage } from 'modules/alerts/state';
import { RootState } from 'store';
import { eventApi, eventSeriesApi } from 'store/apis/event';
import { newIdempotencyKey } from 'store/apis/event/utils';

import { EventWizardFormSerializedValues } from '../../validation';
import { CreateEventEntitiesArgs } from '../types';
import { updateImages } from './updateImages';
import { updateQuestions } from './updateQuestions';

export const createEvent = createAsyncThunk<{ eventId: number }, EventWizardFormSerializedValues>(
  '[Event Wizard] Create Event',
  async (formValues, { dispatch }) => {
    // One key for this save. The tag categories now ride on the create call itself, so an automatic
    // retry must resume rather than create a second event.
    const idempotencyKey = newIdempotencyKey();
    const { eventId, seriesId } = await dispatch(initializeNewEvent({ formValues, idempotencyKey })).unwrap();
    await dispatch(createEventEntities({ formValues, eventId, seriesId })).unwrap();
    return { eventId };
  }
);

const initializeNewEvent = createAsyncThunk<
  { eventId: number; seriesId?: number },
  { formValues: EventWizardFormSerializedValues; idempotencyKey: string }
>('[Event Wizard] Initialize New Event', async ({ formValues, idempotencyKey }, { dispatch, getState }) => {
  const communityId = selectMyCommunityId(getState() as RootState);

  if (!communityId) {
    dispatch(addAppErrorMessage('Error occurred while creating an Event'));
    logger.error('Community ID missing while creating an Event');
    throw new Error('Community ID missing while creating an Event');
  }

  if (formValues.schedule.length > 1) {
    const { events, id } = await dispatch(
      eventSeriesApi.endpoints.createEventSeries.initiate({
        ...formValues,
        schedule: orderBy(formValues.schedule, 'startTime', 'asc'),
        relatedImageIds: [],
        communityId,
        idempotencyKey,
      })
    ).unwrap();
    return { eventId: events[0].id, seriesId: id };
  }

  const { id } = await dispatch(
    eventApi.endpoints.createEvent.initiate({ ...formValues, relatedImageIds: [], communityId, idempotencyKey })
  ).unwrap();
  return { eventId: id };
});

export const createEventEntities = createAsyncThunk<void, CreateEventEntitiesArgs>(
  '[Event Wizard] Create Event Entities',
  async ({ formValues, eventId, seriesId }, { dispatch }) => {
    await dispatch(updateQuestions({ eventId, seriesId, values: formValues })).unwrap();
    await dispatch(updateImages({ eventId, seriesId, values: formValues })).unwrap();
  }
);
